import Exception from "./exception/Exception";
import AbstractSession from "./session/AbstractSession";
import {
	Information,
	PortalSignatureEditProfile,
	UserCredentials
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import FunctionHelper from "./helper/FunctionHelper";
import AbstractPage from "./AbstractPage";

/**
 * An AbstractContext bundles important variables and endpoints which must be accessed from all around the website
 */
export default abstract class AbstractContext<C extends AbstractContext<C, S>, S extends AbstractSession<C, S>> {
	protected _session?: S;
	protected _initialized: boolean;

	/**
	 * Constructs an {@link AbstractContext} instance
	 */
	constructor() {
		this._initialized = false;
	}

	/**
	 * Initializes an {@link AbstractContext} with its associated {@link AbstractPage}
	 */
	public abstract initialize(page: AbstractPage<any, any>): Promise<void>;

	/**
	 * Returns the associated {@link AbstractPage}
	 */
	public getPage(): AbstractPage<any, any> {
		throw new Exception("Context not initialized");
	}

	/**
	 * Sets the current {@link UserCredentials}
	 */
	public set user(user: UserCredentials | undefined) {
		if (!FunctionHelper.exists(this._session)) {
			throw new Exception("Context not initialized");
		}

		this._session!.setUser(user);
	}

	/**
	 * Returns the current {@link UserCredentials}
	 */
	public get user(): UserCredentials | undefined {
		if (!FunctionHelper.exists(this._session)) {
			throw new Exception("Context not initialized");
		}

		return this._session!.getUser();
	}

	/**
	 * Returns the currently active {@link AbstractSession}
	 */
	public get session(): S {
		if (!FunctionHelper.exists(this._session)) {
			throw new Exception("Context not initialized");
		}

		return this._session!;
	}

	/**
	 * Fetches and returns new portal {@link Information} from server
	 */
	public async fetchInfo(): Promise<Information> {
		let Information: Information = await this.session.getInfo().fetch();

		this.getPage().setState({
			signatureEditProfile: FunctionHelper.applyValue(
				Information.settings?.signature?.signatureEditProfile, PortalSignatureEditProfile.AdobeRestrictions
			)
		});

		return Information;
	}

	/**
	 * Returns the portal {@link Information}
	 */
	public get info(): Information {
		return this.session.getInfo().get();
	}

	/**
	 * Returns if this {@link AbstractContext} is initialized
	 */
	public get initialized(): boolean {
		return this._initialized;
	}
}